/**
 * FE mirror of backend SELF_SERVE_ENABLED_COUNTRIES.
 * Backend remains the authority; this only drives UX.
 */

import {
  getCountryCapability,
  listSupportedCountries,
  normalizeCountryCode,
} from "../utils/country-capabilities.js";

const COUNTRY_LABELS = Object.freeze({
  BR: "Brasil",
  PT: "Portugal",
  ES: "Espanha",
});

/**
 * @returns {string[]}
 */
export function getSelfServeEnabledCountries() {
  const raw = window.CONFIG?.FEATURE_FLAGS?.selfServe?.enabledCountries;
  const list = Array.isArray(raw) ? raw : ["BR"];
  const normalized = [];
  for (const item of list) {
    const code = normalizeCountryCode(item);
    if (code && !normalized.includes(code)) {
      normalized.push(code);
    }
  }
  return normalized.length ? normalized : ["BR"];
}

/**
 * Intersection of feature flag allowlist and capability registry.
 * @returns {string[]}
 */
export function listSignupCountries() {
  const enabled = new Set(getSelfServeEnabledCountries());
  return listSupportedCountries().filter((code) => enabled.has(code));
}

/**
 * @param {string} country
 * @returns {boolean}
 */
export function isSelfServeCountryEnabled(country) {
  const code = normalizeCountryCode(country);
  if (!code) return false;
  return getSelfServeEnabledCountries().includes(code);
}

/**
 * @param {string} country
 * @returns {string}
 */
export function countryDisplayLabel(country) {
  const code = normalizeCountryCode(country) || "BR";
  return COUNTRY_LABELS[code] || code;
}

/**
 * Locale sent on intent (PT explicit pt-PT; others registry default).
 * @param {string} country
 * @returns {string|undefined}
 */
export function signupLocaleForCountry(country) {
  const code = normalizeCountryCode(country) || "BR";
  if (code === "PT") return "pt-PT";
  if (code === "BR") return "pt-BR";
  const caps = getCountryCapability(code);
  return caps?.defaultLocale || undefined;
}

/**
 * @param {string} country
 * @returns {{ country: string, label: string, taxIdType: string, phoneRegion: string, currency: string }}
 */
export function signupChromeForCountry(country) {
  const code = normalizeCountryCode(country) || "BR";
  const caps = getCountryCapability(code);
  return {
    country: code,
    label: countryDisplayLabel(code),
    taxIdType: String(caps.taxIdType || "CNPJ"),
    phoneRegion: String(caps.phoneRegion || code),
    currency: String(caps.billingCurrency || caps.currency || "BRL"),
  };
}
